// src/hooks/useSchedulerTasks.ts
// ============================================
// useSchedulerTasks - Hermes 任务调度数据 Hook
//
// 职责：
//   1. 订阅 schedulerStore，向组件暴露任务/历史/操作方法
//   2. 每 5 秒自动轮询 fetchTasks + fetchHistory（间隔可配置）
//   3. 组件卸载时停止轮询
//   4. 派生 hermesTasks / otherTasks（避免组件重复计算）
//
// 返回：tasks / hermesTasks / otherTasks / history / loading /
//       triggerTask / pauseTask / resumeTask / refresh
// ============================================

import { useEffect, useMemo } from "react";
import { useSchedulerStore } from "../stores/schedulerStore";
import type {
  ScheduledTask,
  TaskExecutionRecord,
} from "../runtime/SchedulerRuntime/types";

// Hermes 节奏任务 ID 前缀
const HERMES_RHYTHM_PREFIX = "builtin.hermes-rhythm-";

/** 默认轮询间隔：5 秒 */
const DEFAULT_INTERVAL_MS = 5000;

export interface UseSchedulerTasksResult {
  /** 所有调度任务 */
  tasks: ScheduledTask[];
  /** Hermes 节奏任务 */
  hermesTasks: ScheduledTask[];
  /** 非 Hermes 节奏的内置任务 */
  otherTasks: ScheduledTask[];
  /** 执行历史 */
  history: TaskExecutionRecord[];
  /** 加载状态 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 最后更新时间 */
  lastUpdated: string | null;
  /** 自动刷新开关 */
  autoRefresh: boolean;
  /** 正在触发中的任务 ID 列表 */
  triggeringTaskIds: string[];
  /** 手动触发任务 */
  triggerTask: (taskId: string) => Promise<void>;
  /** 暂停任务 */
  pauseTask: (taskId: string) => Promise<void>;
  /** 恢复任务 */
  resumeTask: (taskId: string) => Promise<void>;
  /** 立即刷新（任务 + 历史） */
  refresh: () => Promise<void>;
  /** 设置自动刷新开关 */
  setAutoRefresh: (on: boolean) => void;
  /** 清空本地展示的执行历史 */
  clearHistory: () => void;
}

export interface UseSchedulerTasksOptions {
  /** 轮询间隔（毫秒），默认 5000 */
  intervalMs?: number;
}

export function useSchedulerTasks(
  options?: UseSchedulerTasksOptions
): UseSchedulerTasksResult {
  const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;

  // 订阅 store 字段
  const tasks = useSchedulerStore((s) => s.tasks);
  const history = useSchedulerStore((s) => s.history);
  const loading = useSchedulerStore((s) => s.loading);
  const error = useSchedulerStore((s) => s.error);
  const lastUpdated = useSchedulerStore((s) => s.lastUpdated);
  const autoRefresh = useSchedulerStore((s) => s.autoRefresh);
  const triggeringTaskIds = useSchedulerStore((s) => s.triggeringTaskIds);
  const fetchTasks = useSchedulerStore((s) => s.fetchTasks);
  const fetchHistory = useSchedulerStore((s) => s.fetchHistory);
  const triggerTask = useSchedulerStore((s) => s.triggerTask);
  const pauseTask = useSchedulerStore((s) => s.pauseTask);
  const resumeTask = useSchedulerStore((s) => s.resumeTask);
  const setAutoRefresh = useSchedulerStore((s) => s.setAutoRefresh);
  const clearHistory = useSchedulerStore((s) => s.clearHistory);

  // 首次加载 + 自动轮询
  useEffect(() => {
    // 首次立即拉取
    fetchTasks();
    fetchHistory();
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      fetchTasks();
      fetchHistory();
    }, intervalMs);
    // 组件卸载时停止轮询
    return () => clearInterval(timer);
  }, [fetchTasks, fetchHistory, intervalMs, autoRefresh]);

  // 派生：Hermes 节奏任务 / 其他任务
  const hermesTasks = useMemo(
    () => tasks.filter((t) => t.id.startsWith(HERMES_RHYTHM_PREFIX)),
    [tasks]
  );
  const otherTasks = useMemo(
    () => tasks.filter((t) => !t.id.startsWith(HERMES_RHYTHM_PREFIX)),
    [tasks]
  );

  // 立即刷新
  const refresh = async () => {
    await Promise.all([fetchTasks(), fetchHistory()]);
  };

  return {
    tasks,
    hermesTasks,
    otherTasks,
    history,
    loading,
    error,
    lastUpdated,
    autoRefresh,
    triggeringTaskIds,
    triggerTask,
    pauseTask,
    resumeTask,
    refresh,
    setAutoRefresh,
    clearHistory,
  };
}

export default useSchedulerTasks;
